import { css } from '@emotion/react'
import { Icon } from '@iconify/react'
import {
  AppShell,
  BurgerButton,
  ButtonProfile,
  ButtonProfileOption,
  Footer,
  Header,
  IconButton,
  NavBar,
  NavLink,
  ScrollView,
  ServiceLogo,
  Stack,
  theme,
  Typography,
  useResponsive,
} from '@nexans/react-ui-kit'
import { changeLanguage } from 'i18next'
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useToggle } from 'usehooks-ts'

import ForceCountryDialog from '@/components/elements/ForceCountryDialog'
import { ScreenLoader } from '@/components/feedbacks'
import { APP_SUBTITLE, APP_TITLE } from '@/config/constants'
import { APP_ENV, APP_VERSION } from '@/config/env'
import { getHelpUrl } from '@/config/help'
import { getLegalNoticeUrl } from '@/config/legal'
import { useSignoutUser } from '@/features/auth'
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice'
import { logger } from '@/lib/logger'
import { NAVBAR_LINKS, ROUTER_PRIVATE_LINKS } from '@/router/config'
import { useAppStore } from '@/stores/appStore'
import { useServicesStore } from '@/stores/servicesStore'

function MainLayout() {
  const [open, toggleOpen, setOpen] = useToggle()
  const { isMobile, isSmallTablet, isTablet } = useResponsive()
  const isTouchDevice = useIsTouchDevice()

  const isSmallDevice = isMobile || isSmallTablet
  const footerPadding = isSmallDevice ? 0 : isTablet ? theme.componentsSizes.navbar.tablet : theme.componentsSizes.navbar.desktop

  const loading = useAppStore((state) => state.isLoading)
  const ability = useAppStore((state) => state.ability)
  const user = useAppStore((state) => state.user)
  const services = useServicesStore((state) => state.services)
  const hasResellers = useAppStore((state) => Boolean(state.resellers?.length))

  const { t, i18n } = useTranslation(['common', 'enums'])
  const { pathname } = useLocation()
  const navigate = useNavigate()

  const [forceCountryDialogOpened, setForceCountryDialogOpened] = useState(false)

  const { mutateAsync: signoutUser } = useSignoutUser({
    config: {
      onSuccess: () => {
        navigate('/')
      },
    },
  })

  const handleOnEnterMenu = () => {
    if (!isMobile) {
      setOpen(true)
    }
  }

  const handleOnLeaveMenu = () => {
    if (!isMobile) {
      setOpen(false)
    }
  }

  const handleOnNavigate = (path: string) => {
    logger.info('User navigated via navbar', {
      from: pathname,
      to: path,
      feature: 'navigation',
    })

    if (isMobile) {
      setOpen(false)
    }
    navigate(path)
  }

  const Logo = useMemo(
    () => (
      <ServiceLogo
        css={{ cursor: 'pointer', marginInlineEnd: !isSmallDevice ? 48 : undefined }}
        size={28}
        subtitle={!isSmallDevice ? APP_SUBTITLE() : undefined}
        title={!isSmallDevice ? APP_TITLE : ''}
        onClick={() => navigate('/')}
      />
    ),
    [navigate, isSmallDevice]
  )

  const legalNoticeUrl = useMemo(() => {
    const lang = i18n.language?.split('-')[0] || 'en'

    return getLegalNoticeUrl(lang)
  }, [i18n.language])

  const helpUrl = useMemo(() => getHelpUrl(), [])

  const handleSignout = async () => {
    logger.info('User logged out via navbar', {
      feature: 'auth',
      action: 'logout',
    })
    await signoutUser(undefined)
  }

  const userOptions: ButtonProfileOption[] = [
    {
      label: t('common:navbar.profile'),
      icon: <Icon height={20} icon="mdi:person-outline" width={20} />,
      color: 'primary',
      onClick: () => {
        navigate(ROUTER_PRIVATE_LINKS.profile.path)
        return true
      },
    },
    {
      label: t('common:navbar.lang'),
      items: [
        {
          label: t('enums:enum.language.EN'),
          icon: <Icon icon="flag:gb-4x3" />,
          onClick: () => {
            changeLanguage('en')
          },
        },
        {
          label: t('enums:enum.language.FR'),
          icon: <Icon icon="flag:fr-4x3" />,
          onClick: () => {
            changeLanguage('fr')
          },
        },
        {
          label: t('enums:enum.language.ES'),
          icon: <Icon icon="flag:es-4x3" />,
          onClick: () => {
            changeLanguage('es')
          },
        },
      ],
    },
    ...(['local', 'dev', 'qal'].includes(APP_ENV)
      ? ([
          {
            label: t('common:navbar.force_country'),
            icon: <Icon height={20} icon="mdi:world-wide-web" width={20} />,
            color: 'primary',
            onClick: () => {
              setForceCountryDialogOpened(true)
              return true
            },
          },
        ] as ButtonProfileOption[])
      : []),
    {
      label: t('common:navbar.logout'),
      icon: <Icon height={20} icon="mdi:logout" width={20} />,
      color: 'destructive',
      onClick: () => {
        handleSignout()
      },
    },
  ]

  return (
    <>
      {!loading ? (
        <AppShell
          footer={
            <Footer css={{ backgroundColor: theme.colors.gray[0] }}>
              <Stack
                css={{ paddingInlineStart: footerPadding + 24 }}
                direction="horizontal"
                justify="space-between"
                spacing="sm"
                fullWidth
              >
                <Typography variant="body1" noWrap>
                  @Mobiway - 2025 · v{APP_VERSION}
                </Typography>
                <NavLink css={{ whiteSpace: 'nowrap' }} href={legalNoticeUrl} target="_blank">
                  {t('common:footer.legal_notice')}
                </NavLink>
              </Stack>
            </Footer>
          }
          header={
            <Header>
              <Stack align="center" direction="horizontal" justify="space-between" fullWidth>
                <Stack align="center" direction="horizontal" spacing="md" fullWidth>
                  {(isTouchDevice || isSmallDevice) && <BurgerButton opened={open} onClick={toggleOpen} />}
                  {Logo}
                </Stack>
                <Stack align="center" direction="horizontal" justify="flex-end" spacing="md" fullWidth>
                  <ButtonProfile
                    avatarBackgroundColor={theme.colors.red[50]}
                    avatarColor={theme.colors.red[800]}
                    css={{ flex: 1 }}
                    firstName={user?.firstName}
                    info={user?.email}
                    lastName={user?.lastName}
                    options={userOptions}
                    variant="avatar-button"
                  />
                  {helpUrl && (
                    <NavLink css={{ whiteSpace: 'nowrap' }} href={helpUrl} target="_blank">
                      <IconButton
                        icon={
                          <Icon
                            height={24}
                            icon="mdi:question-mark-circle"
                            style={{ color: theme.colors.primary[500] }}
                            width={24}
                          />
                        }
                        size="medium"
                        onClick={() => {}}
                      />
                    </NavLink>
                  )}
                </Stack>
              </Stack>
            </Header>
          }
          navbar={
            <NavBar expanded={open} contained onEnter={handleOnEnterMenu} onLeave={handleOnLeaveMenu}>
              {NAVBAR_LINKS.map((link) => (
                <Fragment key={link.label}>
                  {/* Root nav links */}
                  {link.path &&
                    ability.can(link.ability.action, link.ability.screen) &&
                    !(APP_ENV === 'prd' && !link.isProd) &&
                    (link.path !== ROUTER_PRIVATE_LINKS.spareparts.path || services?.spareParts === true) &&
                    (link.path !== ROUTER_PRIVATE_LINKS.store.path || hasResellers) && (
                      <NavBar.Item
                        active={pathname.startsWith(link.path)}
                        expanded={open}
                        routerLink={{ ...link, label: t(link.label) }}
                        onNavigate={handleOnNavigate}
                      />
                    )}
                </Fragment>
              ))}
              <div style={{ flex: 1 }} />
              <NavBar.Item
                active={false}
                expanded={open}
                routerLink={{
                  icon: <Icon height="24" icon="mdi:logout" width="24" />,
                  label: t('common:navbar.logout'),
                  path: '/#',
                }}
                borderTop
                onNavigate={handleSignout}
              />
            </NavBar>
          }
          contained
        >
          <Stack css={styles.main}>
            <ScrollView styleContainer={{ flex: 1, width: '100%' }}>
              <Outlet />
            </ScrollView>
          </Stack>
        </AppShell>
      ) : (
        <ScreenLoader />
      )}

      <ForceCountryDialog
        opened={forceCountryDialogOpened}
        onClose={() => {
          setForceCountryDialogOpened(false)
        }}
      />
    </>
  )
}

export default MainLayout

const styles = {
  main: css({
    flex: 1,
    position: 'relative',
    height: `calc(100vh - ${theme.componentsSizes.header}px - ${theme.componentsSizes.footer}px)`,
    maxHeight: `calc(100vh - ${theme.componentsSizes.header}px - ${theme.componentsSizes.footer}px)`,
    width: '100%',
    /*background: `linear-gradient(133deg, rgba(255, 193, 142, 0) 51.56%, rgba(255, 193, 142, 0.97) 96.6%),
    linear-gradient(315deg, rgba(255, 122, 103, 0) 2.68%, rgba(255, 122, 103, 0.66) 92.68%),
    linear-gradient(45deg, rgba(255, 223, 231, 0) 66.43%, #ffdfe7 94.64%),
    linear-gradient(225deg, rgba(157, 0, 0, 0) 20.54%, #9d0000 96.61%), #ffac67`,*/
    backgroundColor: theme.colors.gray[25],
  }),
}
